import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { amiGet, Card, Kpi, Loading, NotYet, num, pct, tsFmt, errMsg } from "@/lib/ami";

const INSTS = [{ key: "BTC-USDT-SWAP", label: "Perpetual Swap" }, { key: "BTC-USDT", label: "Spot" }];
const BARS = ["1m", "5m", "15m", "1h", "4h", "1d", "1w"];

function Toggle({ items, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-full border border-border p-1 bg-secondary/40" data-testid={testid}>
      {items.map((i) => (
        <button key={i.key} onClick={() => onChange(i.key)} data-testid={`${testid}-${i.key}`}
          className={`px-3 py-1 rounded-full text-xs transition-colors ${value === i.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

export function PriceChart({ inst, bar, height = 280 }) {
  const q = useQuery({ queryKey: ["ami-candles", inst, bar], queryFn: () => amiGet("/candles", { inst, bar, limit: 300 }), refetchInterval: 60000 });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <NotYet text={errMsg(q.error)} />;
  const data = q.data.candles.map((c) => ({ t: c.ts, c: c.c }));
  return (
    <div data-testid="ami-price-chart">
      <div className="text-xs text-muted-foreground mb-2">Quelle: {q.data.source === "datenbank" ? "gespeicherte Historie" : "OKX live (noch keine Historie importiert)"} · {data.length} Kerzen</div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="t" tickFormatter={(t) => new Date(t).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" minTickGap={40} />
          <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={70} tickFormatter={(v) => num(v, 0)} />
          <Tooltip labelFormatter={tsFmt} formatter={(v) => [num(v, 1) + " $", "Schluss"]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
          <Line type="monotone" dataKey="c" stroke="hsl(var(--primary))" strokeWidth={1.75} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AmiMarket() {
  const [inst, setInst] = useState("BTC-USDT-SWAP");
  const [bar, setBar] = useState("1h");
  const q = useQuery({ queryKey: ["ami-market", inst], queryFn: () => amiGet("/market", { inst }), refetchInterval: 5000 });
  const m = q.data;
  return (
    <div className="space-y-6" data-testid="ami-market">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Toggle items={INSTS} value={inst} onChange={setInst} testid="ami-inst-toggle" />
        <span className="text-xs text-muted-foreground" data-testid="ami-market-updated">{m ? `Stand ${tsFmt(m.ts)} · alle 5 s` : ""}</span>
      </div>
      {q.isError && <NotYet text={errMsg(q.error)} testid="ami-market-error" />}
      {!m ? <Loading /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label={`${inst} Kurs`} value={`${num(m.last, 1)} $`} hint={`Bid ${num(m.bid, 1)} · Ask ${num(m.ask, 1)}`} testid="ami-kpi-last" />
          <Kpi label="24 h Veränderung" value={pct(m.change24h_pct)} tone={m.change24h_pct >= 0 ? "pos" : "neg"} hint={`H ${num(m.high24h, 0)} · T ${num(m.low24h, 0)}`} />
          <Kpi label="Realisierte Vol. 24 h / 72 h" value={`${num(m.volatility?.realized_24h_ann_pct, 1)} / ${num(m.volatility?.realized_72h_ann_pct, 1)} %`} hint="annualisiert, aus 1H-Kerzen" />
          <Kpi label="ATR(14) 1H" value={`${num(m.volatility?.atr14_1h, 1)} $`} hint={`${num(m.volatility?.atr14_1h_pct, 2)} % vom Kurs`} />
          <Kpi label="Orderbuch-Imbalance" value={m.orderbook ? `${num(m.orderbook.imbalance * 100, 1)} %` : "–"} hint={m.orderbook ? `±1 % · Bid ${num(m.orderbook.bid_size, 0)} / Ask ${num(m.orderbook.ask_size, 0)} ${m.orderbook.unit}` : ""} testid="ami-kpi-imbalance" />
          <Kpi label="Spread" value={m.orderbook ? `${num(m.orderbook.spread, 1)} $` : "–"} />
          <Kpi label="Funding-Rate" value={m.funding ? `${num(m.funding.rate * 100, 4)} %` : "nur Swap"} hint={m.funding ? `nächste ${tsFmt(m.funding.funding_time)}` : ""} />
          <Kpi label="Open Interest" value={m.open_interest ? `${num(m.open_interest.btc, 0)} BTC` : "nur Swap"} hint={m.open_interest ? `${num(m.open_interest.usd / 1e9, 2)} Mrd. $` : ""} />
        </div>
      )}
      <Card title="Kursverlauf" testid="ami-chart-card" actions={<Toggle items={BARS.map((b) => ({ key: b, label: b }))} value={bar} onChange={setBar} testid="ami-bar-toggle" />}>
        <PriceChart inst={inst} bar={bar} />
      </Card>
      <p className="text-xs text-muted-foreground">Alle Werte sind direkte Beobachtungen bzw. einfache Statistiken aus OKX-Daten – keine Prognosen.</p>
    </div>
  );
}
